import { QA_COLORS } from "./qaAnalysis";
import type { QAType } from "./qaAnalysis";

export const BLOCK_TAGS = new Set(["P", "LI", "TD", "TH", "H1", "H2", "H3", "H4", "H5", "H6"]);
export const HEADING_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6"]);

const CONDITIONAL_MARKER_RE = /^\s*conditional\b[:\s]|include\s+(this\s+)?if\b|omit\s+if\b|include\s+only\s+if\b|include\s+when\b/i;

const SLOT_KEYWORDS_RE = /^\s*(insert|use the|use today|enter|provide|state|document the (?:client salutation|address|date))/i;
const SLOT_MAX_LEN = 200;

// Indicators that a blockquote contains a substantive generation instruction
// (per template_generation_document.au.md: "Document [content] covering X, Y, Z").
const GENERATION_INDICATOR_RE = /\b(covering|including|addressing|subsections?|narrative\s+prose|bullet\s+points?|document\s+(a|the)\s+(client'?s|recommended|basis|reasons|risks|alternatives|summary|rationale|outcomes?|circumstances|strategies|executive\s+summary)|describe\b|outline\s+(the|every)|provide\s+(an\s+overview|brief|a\s+summary))\b/i;

// Strip a leading "Conditional Section: …" or "Conditional: …" lead line so we
// can judge whether the remaining content is a generation instruction.
function withoutConditionalLead(text: string): string {
  return text.replace(/^\s*conditional[^.\n]*[.\n]\s*/i, "").trim();
}

const DEVIATION_COLOR = "#DC6A4C";

function normalize(s: string): string {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}

export type FlatBlock = {
  tag: string;
  text: string;
  el: HTMLElement;
};

export function flattenBlocks(html: string): { blocks: FlatBlock[]; body: HTMLElement } {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const blocks: FlatBlock[] = [];

  function flatten(el: Element) {
    if (el.tagName === "BLOCKQUOTE" || BLOCK_TAGS.has(el.tagName)) {
      blocks.push({
        tag: el.tagName,
        text: normalize(el.textContent ?? ""),
        el: el as HTMLElement,
      });
      return;
    }
    for (const c of Array.from(el.children)) flatten(c);
  }

  for (const c of Array.from(doc.body.children)) flatten(c);
  return { blocks, body: doc.body as HTMLElement };
}

function isConditionalMarker(text: string): boolean {
  return CONDITIONAL_MARKER_RE.test(text);
}

function isSlotBlockquote(text: string): boolean {
  return text.length <= SLOT_MAX_LEN || SLOT_KEYWORDS_RE.test(text);
}

// True if the blockquote text instructs the LLM to GENERATE substantive content
// (not just substitute a value, not just describe a conditional). This is the
// thing that makes a section's CONTENT TYPE variable, regardless of whether the
// section is conditionally included.
function isVariableInstruction(text: string): boolean {
  const body = withoutConditionalLead(text);
  if (body.length === 0) return false;
  if (GENERATION_INDICATOR_RE.test(body)) return true;
  if (body.length >= 250) return true;
  return false;
}

// True if the blockquote is ONLY a conditional marker (controls inclusion) and
// does not also carry a generation instruction.
function isPureConditionalMarker(text: string): boolean {
  if (!CONDITIONAL_MARKER_RE.test(text)) return false;
  return !isVariableInstruction(text);
}

export type TemplateRegion = {
  type: QAType;
  fixedText: string;
  blockIndices: number[];
};

export function collectTemplateRegions(blocks: FlatBlock[]): TemplateRegion[] {
  const regions: TemplateRegion[] = [];
  let inConditional = false;
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i];
    if (!block) { i++; continue; }

    if (HEADING_TAGS.has(block.tag)) {
      inConditional = false;
      if (block.text.length > 0) {
        regions.push({ type: "fixed", fixedText: block.text, blockIndices: [i] });
      }
      i++;
      continue;
    }

    if (block.tag === "BLOCKQUOTE") {
      if (isConditionalMarker(block.text)) {
        inConditional = true;
        i++;
        continue;
      }
      regions.push({
        type: inConditional ? "fixed-conditional" : "variable",
        fixedText: "",
        blockIndices: [i],
      });
      i++;
      continue;
    }

    if (block.text.length === 0) { i++; continue; }

    const nextBlock = blocks[i + 1];
    const nextIsSlot = nextBlock
      && nextBlock.tag === "BLOCKQUOTE"
      && !isConditionalMarker(nextBlock.text)
      && isSlotBlockquote(nextBlock.text);

    if (nextIsSlot) {
      const fixedParts: string[] = [block.text];
      const indices: number[] = [i];
      let j = i + 1;
      while (j < blocks.length) {
        const b = blocks[j];
        if (!b) break;
        if (b.tag === "BLOCKQUOTE" && !isConditionalMarker(b.text) && isSlotBlockquote(b.text)) {
          indices.push(j);
          j++;
          continue;
        }
        if (b.tag !== "BLOCKQUOTE" && BLOCK_TAGS.has(b.tag) && !HEADING_TAGS.has(b.tag) && b.text.length > 0) {
          const after = blocks[j + 1];
          const followedBySlot = after
            && after.tag === "BLOCKQUOTE"
            && !isConditionalMarker(after.text)
            && isSlotBlockquote(after.text);
          if (followedBySlot) {
            fixedParts.push(b.text);
            indices.push(j);
            j++;
            continue;
          }
        }
        break;
      }
      regions.push({
        type: inConditional ? "fixed-conditional" : "fixed-variable",
        fixedText: fixedParts.join(" "),
        blockIndices: indices,
      });
      i = j;
      continue;
    }

    regions.push({
      type: inConditional ? "fixed-conditional" : "fixed",
      fixedText: block.text,
      blockIndices: [i],
    });
    i++;
  }

  return regions;
}

const MIN_OVERLAP_RATIO = 0.4;
const VERBATIM_RATIO = 0.95;

export function bestMatch(outputText: string, regions: TemplateRegion[]): { region: TemplateRegion | null; score: number } {
  let best: TemplateRegion | null = null;
  let bestScore = 0;

  for (const region of regions) {
    const ft = region.fixedText;
    if (!ft) continue;
    let score = 0;
    if (outputText === ft) {
      score = 1.0;
    } else if (outputText.includes(ft)) {
      score = ft.length / Math.max(1, outputText.length);
    } else if (ft.includes(outputText)) {
      score = outputText.length / Math.max(1, ft.length);
    } else {
      const words = ft.split(/\s+/).filter((w) => w.length >= 4);
      if (words.length > 0) {
        let hits = 0;
        for (const w of words) if (outputText.includes(w)) hits++;
        const wordScore = (hits / words.length) * 0.6;
        if (wordScore > 0.5) score = wordScore;
      }
    }
    if (score > bestScore) {
      best = region;
      bestScore = score;
    }
  }

  return { region: best, score: bestScore };
}

export type ClassifyResult = { type: QAType; deviation: boolean };

export function classifyOutputBlock(outputText: string, regions: TemplateRegion[]): ClassifyResult {
  const { region, score } = bestMatch(outputText, regions);

  if (!region) return { type: "variable", deviation: false };

  const isConditional = region.type === "fixed-conditional";

  if (region.type === "fixed" || region.type === "fixed-conditional") {
    if (score >= VERBATIM_RATIO) {
      return { type: isConditional ? "fixed-conditional" : "fixed", deviation: false };
    }
    if (score >= MIN_OVERLAP_RATIO) {
      return { type: isConditional ? "fixed-conditional" : "fixed-variable", deviation: true };
    }
    return { type: "variable", deviation: false };
  }

  if (region.type === "fixed-variable") {
    if (score >= MIN_OVERLAP_RATIO) {
      return { type: isConditional ? "fixed-conditional" : "fixed-variable", deviation: false };
    }
    return { type: "variable", deviation: false };
  }

  return { type: "variable", deviation: false };
}

function applyStyle(el: HTMLElement, type: QAType, deviation: boolean): void {
  const { bg, border } = QA_COLORS[type];
  if (bg !== "transparent") el.style.background = bg;
  if (border !== "transparent") {
    el.style.borderLeft = `3px solid ${border}`;
    el.style.paddingLeft = "10px";
    el.style.marginLeft = "-13px";
    el.style.borderRadius = "2px";
  }
  el.setAttribute("data-qa", type);
  if (deviation) {
    el.style.outline = `2px dashed ${DEVIATION_COLOR}`;
    el.style.outlineOffset = "2px";
    el.setAttribute("data-deviation", "true");
  }
}

export type Section = {
  heading: string;
  blocks: FlatBlock[];
};

export function splitIntoSections(blocks: FlatBlock[]): Section[] {
  const sections: Section[] = [{ heading: "", blocks: [] }];
  for (const block of blocks) {
    if (HEADING_TAGS.has(block.tag) && block.text.length > 0) {
      sections.push({ heading: block.text, blocks: [block] });
    } else {
      const last = sections[sections.length - 1];
      if (last) last.blocks.push(block);
    }
  }
  return sections;
}

export type OutputAnnotationSummary = Record<QAType, number> & { deviations: number };

export function annotateOutputAgainstTemplate(
  outputHtml: string,
  templateHtml: string,
): { html: string; summary: OutputAnnotationSummary } {
  const tmplFlat = flattenBlocks(templateHtml);
  const outFlat = flattenBlocks(outputHtml);

  const tmplSections = splitIntoSections(tmplFlat.blocks);
  const outSections = splitIntoSections(outFlat.blocks);

  const sectionRegions = new Map<string, TemplateRegion[]>();
  for (const s of tmplSections) {
    if (s.heading) sectionRegions.set(s.heading, collectTemplateRegions(s.blocks));
  }
  const globalRegions = collectTemplateRegions(tmplFlat.blocks);

  const summary: OutputAnnotationSummary = {
    fixed: 0, "fixed-variable": 0, "fixed-conditional": 0, variable: 0, deviations: 0,
  };

  for (const section of outSections) {
    const regions = sectionRegions.get(section.heading) ?? globalRegions;
    for (const ob of section.blocks) {
      if (ob.tag === "BLOCKQUOTE" || ob.text.length === 0 || HEADING_TAGS.has(ob.tag)) continue;
      const { type, deviation } = classifyOutputBlock(ob.text, regions);
      summary[type]++;
      if (deviation) summary.deviations++;
      applyStyle(ob.el, type, deviation);
    }
  }

  return { html: outFlat.body.innerHTML, summary };
}

function sectionClassFromBlocks(blocks: FlatBlock[], start: number, end: number): QAType {
  let hasVariable = false;
  let hasPureConditional = false;
  let hasSlot = false;
  for (let i = start; i < end; i++) {
    const b = blocks[i];
    if (!b || b.tag !== "BLOCKQUOTE") continue;
    if (isVariableInstruction(b.text)) hasVariable = true;
    else if (isPureConditionalMarker(b.text)) hasPureConditional = true;
    else if (isSlotBlockquote(b.text)) hasSlot = true;
    else hasVariable = true;
  }
  if (hasVariable) return "variable";
  if (hasPureConditional) return "fixed-conditional";
  if (hasSlot) return "fixed-variable";
  return "fixed";
}

// Panel 3 classification: roll up each section (H1 / H2 bounded) into a single
// class that reflects what the OUTPUT for that section will look like.
// Reason: the user wants to see "this Executive Summary section is variable"
// rather than "the heading is fixed and the blockquote inside is variable".
export function annotateTemplate(templateHtml: string): { html: string; summary: OutputAnnotationSummary } {
  const { blocks, body } = flattenBlocks(templateHtml);

  const sectionBounds: { start: number; end: number }[] = [];
  let currentStart = 0;
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (!b) continue;
    if ((b.tag === "H1" || b.tag === "H2") && i > currentStart) {
      sectionBounds.push({ start: currentStart, end: i });
      currentStart = i;
    }
  }
  if (currentStart < blocks.length) {
    sectionBounds.push({ start: currentStart, end: blocks.length });
  }

  const summary: OutputAnnotationSummary = {
    fixed: 0, "fixed-variable": 0, "fixed-conditional": 0, variable: 0, deviations: 0,
  };

  for (const { start, end } of sectionBounds) {
    const sectionClass = sectionClassFromBlocks(blocks, start, end);
    summary[sectionClass]++;
    for (let i = start; i < end; i++) {
      const block = blocks[i];
      if (!block) continue;
      applyStyle(block.el, sectionClass, false);
    }
  }

  return { html: body.innerHTML, summary };
}
